import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { formatPhoneDisplay, unformatPhone } from '@/lib/formatters';
import type { InsuredContact } from '@/lib/api';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initial?: InsuredContact;
  onSubmit: (contact: InsuredContact) => void;
  isSaving?: boolean;
}

export function ContactFormDialog({ open, onOpenChange, initial, onSubmit, isSaving }: Props) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [isPrimary, setIsPrimary] = useState(false);

  useEffect(() => {
    if (open) {
      setName(initial?.name ?? '');
      setEmail(initial?.email ?? '');
      setPhone(initial?.phone ?? '');
      setIsPrimary(initial?.isPrimary === true);
    }
  }, [open, initial]);

  const trimmedEmail = email.trim();
  const emailValid = trimmedEmail === '' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail);
  const canSubmit = !isSaving && name.trim() !== '' && emailValid;
  // CSR-1140 — flag an incomplete phone, but never block submit (phone is optional).
  const phoneInvalid = phone.trim() !== '' && phone.replace(/\D/g, '').length !== 10;

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit({
      name: name.trim(),
      email: trimmedEmail || null,
      phone: phone.trim() || null,
      isPrimary,
      createdAt: initial?.createdAt,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{initial ? 'Edit contact' : 'Add contact'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="contact-name">Name</Label>
            <Input
              id="contact-name"
              data-testid="contact-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="contact-email">Email</Label>
            <Input
              id="contact-email"
              data-testid="contact-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={!emailValid ? 'border-red-500' : ''}
            />
            {!emailValid && (
              <p className="mt-1 text-xs text-red-600">Invalid email</p>
            )}
          </div>
          <div>
            <Label htmlFor="contact-phone">Phone</Label>
            <Input
              id="contact-phone"
              data-testid="contact-phone"
              value={formatPhoneDisplay(phone)}
              onChange={(e) => setPhone(unformatPhone(e.target.value))}
              maxLength={14}
              className={phoneInvalid ? 'border-red-500' : ''}
            />
            {phoneInvalid && (
              <p data-testid="contact-phone-error" className="mt-1 text-xs text-red-600">
                Enter a valid 10-digit phone number.
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="contact-primary"
              data-testid="contact-primary"
              checked={isPrimary}
              onCheckedChange={(v) => setIsPrimary(v === true)}
            />
            <Label htmlFor="contact-primary">Primary contact</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="cursor-pointer">
            Cancel
          </Button>
          <Button
            data-testid="contact-submit"
            disabled={!canSubmit}
            onClick={handleSubmit}
            className="cursor-pointer"
          >
            {initial ? 'Save' : 'Add'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
